import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import UserStore from "components/Admin/UserManager/UserStore";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import SendIcon from "@mui/icons-material/Send";
import SearchSubscriptionDialog from "components/Conversations/Views/Drawer/components/Subscriptions/SearchSubscriptionDialog";
import { observer } from "mobx-react";
import React, { useMemo, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import { useSearchParams } from "react-router-dom";
import { ApplicationFiltersService } from "services/ApplicationFiltersService";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import { areFiltersEqual } from "./helpers";
import { getActiveFilters } from "./subscriptionRenderedFilter";
interface SubscriptionHandlerProps {
    applicationFiltersStore: ApplicationFiltersStore;
    conversationStore: ConversationsStore;
    applicationFilterValuesStore: ApplicationFilterValuesStore;
    messageStore: MessageStore;
}

const SubscriptionHandler: React.FC<SubscriptionHandlerProps> = ({
    applicationFiltersStore,
    conversationStore,
    applicationFilterValuesStore,
    messageStore,
}) => {
    const userStore = useStore(UserStore);
    const authStore = useStore(AuthStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);

    const [searchParams] = useSearchParams();

    const unappliedFilters = applicationFiltersStore.toFilterObject();
    const appliedConversationFilters =
        applicationFiltersStore.savedFilterStates["Conversations"];
    const appliedDrilldownFilters =
        applicationFiltersStore.savedFilterStates["after-drilldown"];
    const isFromSignals =
        searchParams.has("fromDomain") &&
        searchParams.get("fromDomain") === "signals" &&
        !!applicationFiltersStore.savedFilterStates["after-drilldown"];

    const currentlyAppliedFilters =
        (isFromSignals
            ? appliedDrilldownFilters
            : appliedConversationFilters) ?? unappliedFilters;

    const { selectedSavedFilter } = applicationFiltersStore;
    const isFilterCreator = Boolean(
        selectedSavedFilter?.userId === userStore.appUser?.id,
    );

    const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] =
        useState(false);

    const [isSendingImmediate, setIsSendingImmediate] = useState(false);
    // The sanitizer drops Question Result rows this viewer cannot see, so both
    // sides of the comparison must recompute once the module catalog resolves.
    const questionResultModuleAccessKey =
        applicationFilterValuesStore.questionResultModuleAccessKey;

    const comparableSelectedSavedFilter = useMemo(
        () => {
            // Read so exhaustive-deps accepts the key below: the real dependency
            // runs through mobx state inside stripInaccessibleFilters, where the
            // linter cannot see it.
            void questionResultModuleAccessKey;

            return selectedSavedFilter?.value
                ? applicationFilterItemsStore.stripInaccessibleFilters(
                      selectedSavedFilter.value,
                  ).filters
                : undefined;
        },
        [
            selectedSavedFilter?.value,
            applicationFilterItemsStore,
            questionResultModuleAccessKey,
        ],
    );
    const comparableCurrentlyAppliedFilters = useMemo(
        () => {
            // Read so exhaustive-deps accepts the key below: the real dependency
            // runs through mobx state inside stripInaccessibleFilters, where the
            // linter cannot see it.
            void questionResultModuleAccessKey;

            return currentlyAppliedFilters
                ? applicationFilterItemsStore.stripInaccessibleFilters(
                      currentlyAppliedFilters,
                  ).filters
                : undefined;
        },
        [
            currentlyAppliedFilters,
            applicationFilterItemsStore,
            questionResultModuleAccessKey,
        ],
    );

    const isSavedFilterApplied = areFiltersEqual(
        comparableSelectedSavedFilter,
        comparableCurrentlyAppliedFilters,
    );

    const isSubscribedToAppliedFilters =
        !!selectedSavedFilter?.applicationFilterSubscriptionId &&
        isSavedFilterApplied;

    const areCurrentFiltersApplied = areFiltersEqual(
        currentlyAppliedFilters,
        unappliedFilters,
    );

    const hasModifiedFilters =
        getActiveFilters(unappliedFilters, applicationFiltersStore).length > 0;

    const canSendImmediateSubscriptionEmail =
        authStore.isUserUltra() ||
        (window as any).__conversations_allowImmediateSubscriptionEmail;

    const sendImmediateEmail = async () => {
        if (!selectedSavedFilter?.applicationFilterSubscriptionId) return;
        setIsSendingImmediate(true);
        try {
            await new ApplicationFiltersService().sendImmediateEmail(
                selectedSavedFilter.applicationFilterSubscriptionId,
            );
            messageStore.logInfo(
                "Your email has been scheduled to send. This may take a few minutes.",
            );
        } catch (error) {
            messageStore.logError("Failed to send immediate email.");
        }
        setIsSendingImmediate(false);
    };

    let tooltipTitle = "";

    const isNotOwnerOfModifiedFilter =
        !isFilterCreator && !!selectedSavedFilter && !isSavedFilterApplied;

    if (!areCurrentFiltersApplied || !hasModifiedFilters) {
        tooltipTitle = "You must apply the search before subscribing.";
    } else if (isNotOwnerOfModifiedFilter) {
        tooltipTitle =
            "Only the creator of a filter may modify it. Please save as a new filter to subscribe.";
    } else {
        tooltipTitle = "Click to manage";
    }

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title={tooltipTitle}>
                    <Box component="span" sx={{ display: "block", flex: 1 }}>
                        <Button
                            id="subscribe-to-search-button"
                            variant={
                                isSubscribedToAppliedFilters
                                    ? "text"
                                    : "outlined"
                            }
                            color={
                                isSubscribedToAppliedFilters
                                    ? "primary"
                                    : "secondary"
                            }
                            fullWidth
                            sx={{
                                fontSize: "12px",
                                justifyContent: isSubscribedToAppliedFilters
                                    ? "start"
                                    : "center",
                                px: isSubscribedToAppliedFilters ? 0 : 1,
                            }}
                            startIcon={
                                isSubscribedToAppliedFilters ? (
                                    <NotificationsIcon />
                                ) : (
                                    <NotificationAddIcon />
                                )
                            }
                            disabled={
                                conversationStore.anyTaskLoading ||
                                !areCurrentFiltersApplied ||
                                isNotOwnerOfModifiedFilter ||
                                !hasModifiedFilters
                            }
                            onClick={() => setIsSubscriptionModalOpen(true)}
                        >
                            {isSubscribedToAppliedFilters
                                ? "Subscribed"
                                : "Subscribe"}{" "}
                            to updates for this search
                        </Button>
                    </Box>
                </Tooltip>
                {canSendImmediateSubscriptionEmail &&
                    isSubscribedToAppliedFilters && (
                        <Tooltip title="Send subscription email now">
                            <Button
                                id="send-immediate-email-button"
                                variant="outlined"
                                color="primary"
                                disabled={isSendingImmediate}
                                sx={{
                                    fontSize: "12px",
                                    minWidth: "auto",
                                    px: 1,
                                }}
                                onClick={sendImmediateEmail}
                            >
                                <SendIcon fontSize="small" />
                            </Button>
                        </Tooltip>
                    )}
            </Stack>
            <SearchSubscriptionDialog
                isOpen={isSubscriptionModalOpen}
                onClose={() => setIsSubscriptionModalOpen(false)}
                source="conversations"
                applicationFiltersStore={applicationFiltersStore}
                applicationFilterValuesStore={applicationFilterValuesStore}
                currentlyAppliedFilters={currentlyAppliedFilters}
                messageStore={messageStore}
                selectedSavedFilter={selectedSavedFilter}
            />
        </>
    );
};

const ObservedSubscriptionHandler = observer(SubscriptionHandler);

export default ObservedSubscriptionHandler;
